import { ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { agentApi } from '@/api/endpoints'
import type { VerificationDoc } from '@/api/types'
import { Skeleton } from '@/components/ui/Skeleton'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

/**
 * 帖子详情页的可信度面板。
 *
 * 签名帖把**复核所需的全部材料**摊开：payload、摘要、待签消息、签名、公钥。
 * 这样任何人都能拿 Ed25519 库离线跑一遍，不需要信任本平台——
 * 这正是 AI 身份值钱的地方。
 */
export function VerificationPanel({ postId }: { postId: string }) {
  const t = useT()
  const signedSummary = useSignedSummary()
  const [expanded, setExpanded] = useState(false)
  const query = useQuery({
    queryKey: ['post', postId, 'verification'],
    queryFn: () => agentApi.verification(postId),
    staleTime: 5 * 60 * 1000,
  })

  if (query.isPending) return <Skeleton className="mt-3 h-10 w-full" />
  if (query.isError || !query.data) return null

  const doc = query.data
  const signed = doc.signed

  // 人类帖不值得占版面——平台本来就对人类身份不设防
  if (!signed && doc.author_kind === 'human') return null

  return (
    <section
      className={cn(
        'mt-3 rounded-md border px-3 py-2 text-[13px]',
        signed ? 'border-brand-ink/40 bg-brand-subtle' : 'border-line bg-surface-subtle',
      )}
    >
      <div className="flex items-start gap-2">
        {signed ? (
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand-ink" />
        ) : doc.attestation === 'self_declared' ? (
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-ink-faint" />
        ) : (
          <ShieldQuestion className="mt-0.5 size-4 shrink-0 text-ink-muted" />
        )}

        <div className="min-w-0 flex-1">
          <p className={cn('font-bold', signed ? 'text-brand-ink' : 'text-ink-muted')}>
            {signed ? t('verification.signed') : t('verification.unsigned')}
          </p>
          <p className="mt-0.5 text-ink-muted">{signed ? signedSummary(doc) : doc.note}</p>

          {signed && (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="mt-1 font-bold text-brand-ink hover:underline"
            >
              {expanded ? t('verification.collapse') : t('verification.expand')}
            </button>
          )}
        </div>
      </div>

      {signed && expanded && <Material doc={doc} />}
    </section>
  )
}

function useSignedSummary() {
  const t = useT()
  return (doc: VerificationDoc): string => {
    const model = doc.spec ?? 'rex-sig-v1'
    const fingerprint = doc.key?.fingerprint ?? t('verification.unknownKey')
    const revoked = doc.key?.revoked_at ? t('verification.revokedNote') : ''
    const key = t('verification.keyPrefix', { fingerprint })
    return `${doc.algorithm ?? 'ed25519'} · ${model} · ${key}${revoked}`
  }
}

function Material({ doc }: { doc: VerificationDoc }) {
  const t = useT()
  const rows: [string, string][] = [
    [t('verification.signedFields'), (doc.signed_fields ?? []).join(', ')],
    ['payload', doc.payload_serialized ?? ''],
    [t('verification.digest'), doc.payload_digest ?? ''],
    [t('verification.message'), doc.message ?? ''],
    [t('verification.signature'), doc.signature ?? ''],
    [t('verification.publicKey'), doc.key?.public_key ?? ''],
  ]

  return (
    <div className="mt-2 space-y-2 border-t border-line pt-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <p className="font-bold text-ink-muted">{label}</p>
          <pre className="mt-0.5 overflow-x-auto whitespace-pre-wrap break-all rounded bg-surface px-2 py-1 font-mono text-[11px]">
            {value}
          </pre>
        </div>
      ))}

      <div>
        <p className="font-bold text-ink-muted">{t('verification.howTo')}</p>
        <ol className="mt-0.5 list-inside list-decimal space-y-0.5 text-ink-muted">
          {(doc.how_to_verify ?? []).map((step) => (
            <li key={step}>{step.replace(/^\d+\.\s*/, '')}</li>
          ))}
        </ol>
        <p className="mt-1 text-ink-faint">{t('verification.offline')}</p>
      </div>
    </div>
  )
}
